import { ReactNode } from "react";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "./typography";

type Props = {
  title: string;
  description?: ReactNode;
  startContent?: ReactNode;
  endContent?: ReactNode;
  footer?: ReactNode;
  className?: string;
  onClick?: () => void;
};

/**
 * A generic Callout component that displays a 2-row layout.
 * Row 1: Start section (startContent + title/description) and End section (endContent).
 * Row 2: Footer section (footer).
 */
export const Callout = ({
  title,
  description,
  startContent,
  endContent,
  footer,
  className,
  onClick,
}: Props) => {
  const Component = onClick ? "button" : "div";
  const buttonProps = onClick ? { type: "button" as const } : {};

  return (
    <Component
      {...buttonProps}
      onClick={onClick}
      className={cn(
        "flex w-full flex-col gap-1 rounded-xl bg-white p-2 text-left transition-all",
        onClick && "cursor-pointer hover:bg-neutral-50 hover:shadow-md",
        className,
      )}
    >
      {/* Top section */}
      <div className="flex w-full items-start justify-between">
        {/* Start section */}
        <div className="flex items-center gap-2">
          {/* Start content (e.g., icon) */}
          {startContent && <div className="shrink-0">{startContent}</div>}

          {/* Title and Description */}
          <div className="flex flex-col">
            <Typography weight="semibold" size="sm" className="leading-[22px]">
              {title}
            </Typography>
            {description && (
              <div className="text-primary text-sm leading-[22px] tracking-normal">
                {typeof description === "string" ? (
                  <Typography size="sm">{description}</Typography>
                ) : (
                  description
                )}
              </div>
            )}
          </div>
        </div>

        {/* End content */}
        {endContent && <div className="shrink-0">{endContent}</div>}
      </div>

      {/* Footer */}
      {footer && <div className="w-full">{footer}</div>}
    </Component>
  );
};
